import { Translation } from '.';
import { Event } from '../documents/restaurants/event';
import { ShiftV02Document } from '../documents/restaurants/shiftsV02';

export enum ShiftType {
  RESERVATION = 'reservation',
  TAKEAWAY = 'takeAway',
  GUEST_ORDERING = 'guestOrdering',
  HAPPY_HOURS = 'happyHours',
  ENTRANCE_TICKET = 'entranceTicket',
  COURSE = 'course',
}

export enum BlockType {
  BLOCK = 'block',
  UNBLOCK = 'unblock',
  MAX_GUESTS = 'max_guests',
  CLOSE_TERRACE = 'close_terrace',
  OPEN_TERRACE = 'open_terrace',
}

export type BlockOptions = {
  shifts: null | string[] | { id: string; maxGuests?: number }[];
  type: BlockType;
  uid: string;
  maxGuests?: number;
  countAllGuests?: boolean;
};

export type SlotPerBlockRule = {
  blocks: number[];
  slots: number;
  number?: number; // old
};

export type SlotPerBlock = {
  rules: SlotPerBlockRule[];
  standard: number;
};

export type ReservationLengthRule = {
  index: number;
  length: number;
  minGuests?: number;
  occassions?: string[];
  vip?: boolean;
  specialOccassion?: boolean;
  minLength?: number;
  from?: number;
  till?: number;
};

export type ReservationLength = {
  rules: ReservationLengthRule[];
  standard: number;
  minLength?: number;
  avoidDoubleSeating?: boolean;
};

export type Overwrite = {
  blockReservations?: boolean;
  maxGuests?: number;
  placeAt?: null | 'indoor' | 'outdoor';
  countAllGuests?: boolean;
  closedSpaces?: string[];
};

export type Rabatt =
  | {
      type: 'percentage' | 'amount' | 'deduction';
      percentage?: number;
    }
  | {
      type: 'amount' | 'deduction';
      amount: {
        value: string;
        currency: string;
      };
    };

/**
 * Phase A — fields on Event that Phase C's `writeEventForShift` is allowed to
 * override per shift/date. Identity and type fields are intentionally
 * excluded: overriding `type`/`per`/`optionPer` would silently break the
 * pricing/flow routing downstream. `id` and `edited` make no sense at the
 * override layer.
 */
export type ShiftEventOverride = Partial<
  Omit<Event, 'id' | 'type' | 'per' | 'optionPer' | 'edited'>
>;

/**
 * Phase A — reference from a shift or a reservationDate snapshot into the
 * global `eventsV03` collection, or an explicit declaration that the shift
 * carries a local-only event, or that no event applies to this specific
 * date.
 *
 * A discriminated union is required because the existing Calendar UI already
 * produces three distinct operations (per-date detach, per-date local-only
 * attach, per-date switch) that a single `{eventId, override}` shape cannot
 * represent faithfully.
 *
 * Phase A+B ships this type dormant — `shift.additional.eventV02` stays the
 * source of truth for reads throughout those phases. Phase C flips the flag
 * and switches reads to the resolver that consumes `eventRef`.
 */
export type ShiftEventRef =
  | {
      kind: 'linked';
      eventId: string;
      active?: boolean;
      override?: ShiftEventOverride;
      linkedAt: number;
      linkedBy: string;
    }
  | {
      kind: 'local';
      event: Event;
      linkedAt: number;
      linkedBy: string;
    }
  | {
      kind: 'detached';
      linkedAt: number;
      linkedBy: string;
    };

export interface ReservationShift extends ShiftV02Document {
  additional?: {
    blockedAt: number[];
    slotsPerBlock: SlotPerBlock;
    reservationLength: ReservationLength;

    floorPlan: null | string;
    placeAt: null | 'indoor' | 'outdoor';
    maxGuests?: number;
    hasTerrace?: boolean;
    countAllGuests?: boolean;
    occassions?: string[]; // old
    spaces?: string[]; // old
    menu?: string | null;
    event?: null | {
      id: string;
      title: Record<string, string>;
      description: Record<string, string>;
      amount: number;
      img?: string;
      multipleOptions?: boolean;
    };
    eventV02?: null | Event;
    /**
     * Phase A — reference into `eventsV03` (plus optional local override), or
     * a local-only event, or an explicit detach. Written alongside
     * `eventV02` by Phase A's `writeEventForShift`; consumed by Phase C's
     * resolver once the feature flag flips. Until then, reads go through
     * `eventV02` and this field is ignored.
     */
    eventRef?: ShiftEventRef;
    latestXBlocksForShiftBeginReservable?: number; // if undefined then not used
  };
  lastReservableTime?: {
    days?: number;
    hours?: number;
    time?: string;
  };
  spaces: string[];
  disableFloorPlan?: boolean; // new
  matchBoth?: boolean;
  occassions: string[];
  overwrite?: Overwrite;
  message?: Translation<string>;
  button?: Translation<string>;
  link?: string;
  replacesShift?: string | null;
  commentOnly?: boolean;
  dates?: string[];
}

export interface CourseShift extends ShiftV02Document {
  type: ShiftType.COURSE;
  additional: {
    minGuests?: number;
    maxGuests?: number;
    timeInAdvance?: number;
    message?: Translation<string>;
  };
  occassions: string[];
}

export interface Date<S = ReservationShift> {
  shifts: S[];
  date: string;
  wDay: number;
  holiday: null | string;
  shiftStatistics?: Record<string, Record<string, number>>;
  scoreDiff?: number;
  terraceOpen?: null | boolean;
  showTerrace?: null | boolean;
  addOnStats?: {
    sales: number;
    total: number;
  };
  events?: string[];
  deleted?: string[];
  saison?: string | null;
  deletedV02?: {
    id: string;
    name: string;
    allFutureShifts?: boolean;
    deletedAt: number;
  }[];
  weather?: {
    weather: {
      icon: string;
      code: number;
      description: string;
    };
    low_temp: number;
    max_temp: number;
    temp: number;
    valid_date: string;
  };
}

export interface OldRegularShift {
  id: string;
  name: string;

  start: number;
  service: number;
  close: number;

  floorPlan: string | null;
  blockedAt: number[];
  maxGuests: null | number;
  occassions: string[];
  spaces: string[];
  placeAt: null | 'indoor' | 'outdoor';
  reservationLength: ReservationLength;
  slotsPerBlock: SlotPerBlock;
  ticket: {};
  wDays: number[];
}

export interface GuestOrderingShift extends ShiftV02Document {
  type: ShiftType.GUEST_ORDERING;
  menu: string | null;

  closed?: boolean;
}

export interface TakeAwayShift extends ShiftV02Document {
  type: ShiftType.TAKEAWAY;
  menu: string | null;
  deliveryMethods: ('pickup' | 'delivery')[];
  closed?: boolean;
  advert?: null | {
    active: boolean;
    image?: string | null;
    translations: Record<
      string,
      { title: string; description: string; additionalDescription?: string }
    >;
  };
  message?: Record<string, string>;
}

export interface OffersShift extends ShiftV02Document {
  type: ShiftType.HAPPY_HOURS;
  offers?: HappyHoursOffer[];

  ad?: {
    translations: Record<string, { title: string; description: string }>;
    imageId?: string;
    imageUrl?: string;
  } | null;
  closed?: boolean;
}

export type HappyHoursOffer = {
  id?: string;
  name: string;
  type: 'meal' | 'category';
  reductionType: 'percentage' | 'amount' | 'newPrice';
  value: number;
};

export interface Attachment {
  type: string;
  size: number;
  documentName: string;
  url: string;
  createdAt: number;
  createdBy: string;
}

type Extension<S extends ShiftV02Document> = Partial<
  Omit<S, keyof ShiftV02Document>
>;

export type AnyShift = ShiftV02Document &
  Extension<ReservationShift> &
  Extension<GuestOrderingShift> &
  Extension<TakeAwayShift> &
  Extension<OffersShift>;
